import React, { useState, useEffect } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CalendarIcon } from "lucide-react";
import { fromIsoDate, toIsoDate, formatDate } from "@/lib/format";
import { cn } from "@/lib/utils";

const MONTH_MAP = {
  jan: 1, january: 1, feb: 2, february: 2, mar: 3, march: 3,
  apr: 4, april: 4, may: 5, jun: 6, june: 6, jul: 7, july: 7,
  aug: 8, august: 8, sep: 9, sept: 9, september: 9,
  oct: 10, october: 10, nov: 11, november: 11, dec: 12, december: 12,
};

// Parse a variety of date formats → ISO YYYY-MM-DD, or "" if invalid.
export function parseDateInput(raw) {
  if (!raw) return "";
  const s = String(raw).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const parts = s.split(/[\-/\s.]+/).filter(Boolean);
  if (parts.length !== 3) return "";
  const [a, b, c] = parts;
  const toMonth = (x) => (/^\d+$/.test(x) ? Number(x) : MONTH_MAP[x.toLowerCase()]);
  let year, month, day;
  if (a.length === 4 && /^\d+$/.test(a)) { // YYYY-MM-DD
    year = Number(a); month = toMonth(b); day = Number(c);
  } else if (c.length === 4 && /^\d+$/.test(c)) { // DD-MMM-YYYY or DD-MM-YYYY
    day = Number(a); month = toMonth(b); year = Number(c);
  }
  if (!year || !month || !day || month < 1 || month > 12 || day < 1 || day > 31) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${year}-${pad(month)}-${pad(day)}`;
}

export default function DateInput({ value, onChange, testid, placeholder, className, disabled, required }) {
  const [text, setText] = useState(value ? formatDate(value) : "");

  useEffect(() => {
    setText(value ? formatDate(value) : "");
  }, [value]);

  const commit = (t) => {
    const iso = parseDateInput(t);
    if (iso) {
      onChange?.(iso);
      setText(formatDate(iso));
    } else {
      setText("");
      onChange?.("");
    }
  };

  return (
    <div className={cn("flex gap-1.5", className)}>
      <Input
        type="text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={(e) => commit(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit(e.currentTarget.value); } }}
        placeholder={placeholder || "DD-MM-YYYY or YYYY-MM-DD"}
        className="font-mono-num"
        data-testid={testid}
        disabled={disabled}
        required={required}
      />
      <Popover>
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" size="icon" className="shrink-0" disabled={disabled} aria-label="Open calendar">
            <CalendarIcon className="w-4 h-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="end">
          <Calendar
            mode="single"
            selected={fromIsoDate(value)}
            onSelect={(d) => { if (d) { const iso = toIsoDate(d); setText(formatDate(iso)); onChange?.(iso); } }}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
